/**
 * AdminCommandPalette — ⌘K / Ctrl+K quick navigation across the whole admin.
 */

import { ExternalLink, LayoutDashboard, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { ADMIN_VISITOR_LINK_GROUPS } from "@/components/admin/adminVisitorLinks";
import { useAdminSidebarConfig } from "@/hooks/useAdminSidebarConfig";
import { useIsSuperAdmin } from "@/lib/auth";
import { filterVisibleAdminNavGroups } from "@/lib/adminNavVisibility";

export interface AdminCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DASHBOARD_ANCHORS = [
  {
    hash: "#mux-membership-hub",
    label: "Hub Mux · Membresía",
    hint: "Estado de las 5 superficies con video",
  },
  {
    hash: "#ver-como-visitor",
    label: "Ver como visitor",
    hint: "Rutas públicas con chips de estado",
  },
  {
    hash: "#experience-gaps",
    label: "Gaps de experiencia",
    hint: "AR vs internacional · geo",
  },
] as const;

export function AdminCommandPalette({ open, onOpenChange }: AdminCommandPaletteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = useIsSuperAdmin();
  const { hidden } = useAdminSidebarConfig();

  const visibleGroups = useMemo(
    () => filterVisibleAdminNavGroups({ isSuperAdmin, hidden }),
    [hidden, isSuperAdmin],
  );

  const onAdminDashboard = location.pathname === "/admin" || location.pathname === "/admin/";

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  const goDashboardAnchor = (hash: string) => {
    onOpenChange(false);
    if (onAdminDashboard) {
      window.location.hash = hash.slice(1);
      return;
    }
    navigate(`/admin${hash}`);
  };

  const refreshDashboard = () => {
    onOpenChange(false);
    if (onAdminDashboard) {
      window.dispatchEvent(new CustomEvent("admin-dashboard-refresh"));
      return;
    }
    navigate("/admin");
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Buscar página o acción…" className="font-ios" />
      <CommandList className="font-ios">
        <CommandEmpty className="font-ios">Sin resultados.</CommandEmpty>

        <CommandGroup heading="Panel principal" className="font-ios">
          <CommandItem
            value="Panel principal Resumen de hoy"
            onSelect={() => go("/admin")}
            className="font-ios"
          >
            <LayoutDashboard className="mr-2 h-4 w-4 opacity-80" aria-hidden />
            <span className="flex-1">Resumen de hoy</span>
          </CommandItem>
          {DASHBOARD_ANCHORS.map((anchor) => (
            <CommandItem
              key={anchor.hash}
              value={`${anchor.label} ${anchor.hint}`}
              onSelect={() => goDashboardAnchor(anchor.hash)}
              className="font-ios"
            >
              <LayoutDashboard className="mr-2 h-4 w-4 opacity-60" aria-hidden />
              <span className="flex-1">{anchor.label}</span>
              <span className="ml-3 truncate text-xs text-muted-foreground font-ios">
                {anchor.hint}
              </span>
            </CommandItem>
          ))}
          <CommandItem
            value="Actualizar dashboard refresh"
            onSelect={refreshDashboard}
            className="font-ios"
          >
            <RefreshCw className="mr-2 h-4 w-4 opacity-80" aria-hidden />
            <span className="flex-1">Actualizar dashboard</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {visibleGroups.map((group) => (
          <CommandGroup key={group.label} heading={group.label} className="font-ios">
            {group.items.map((item) => {
              const hasChildren = (item.children?.length ?? 0) > 0;
              if (hasChildren) return null;
              return (
                <CommandItem
                  key={item.to}
                  value={`${item.label} ${item.hint ?? ""}`}
                  onSelect={() => go(item.to)}
                  className="font-ios"
                >
                  <item.icon className="mr-2 h-4 w-4 opacity-80" aria-hidden />
                  <span className="flex-1">{item.label}</span>
                  {item.hint && (
                    <span className="ml-3 truncate text-xs text-muted-foreground font-ios">
                      {item.hint}
                    </span>
                  )}
                </CommandItem>
              );
            })}

            {group.items
              .filter((i) => i.children?.length)
              .flatMap((parent) =>
                parent.children!.map((child) => (
                  <CommandItem
                    key={child.to}
                    value={`${parent.label} ${child.label}`}
                    onSelect={() => go(child.to)}
                    className="font-ios"
                  >
                    <child.icon className="mr-2 h-4 w-4 opacity-60" aria-hidden />
                    <span className="flex-1">{child.label}</span>
                    <span className="ml-3 truncate text-xs text-muted-foreground font-ios">
                      {parent.label}
                    </span>
                  </CommandItem>
                )),
              )}
          </CommandGroup>
        ))}

        <CommandSeparator />
        <CommandGroup heading="Ver como visitor" className="font-ios">
          {ADMIN_VISITOR_LINK_GROUPS.flatMap((group) =>
            group.links.map((link) => (
              <CommandItem
                key={link.to + link.label}
                value={`visitor ${link.label} ${link.hint} ${link.template}`}
                onSelect={() => {
                  onOpenChange(false);
                  window.open(link.to, "_blank", "noopener,noreferrer");
                }}
                className="font-ios"
              >
                <ExternalLink className="mr-2 h-4 w-4 opacity-70" aria-hidden />
                <span className="flex-1">{link.label}</span>
                <span className="ml-3 truncate text-xs text-muted-foreground font-ios">
                  {link.template || group.label}
                </span>
              </CommandItem>
            )),
          )}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Acciones" className="font-ios">
          <CommandItem
            value="Ver sitio público"
            onSelect={() => {
              onOpenChange(false);
              window.open("/", "_blank", "noopener,noreferrer");
            }}
            className="font-ios"
          >
            <ExternalLink className="mr-2 h-4 w-4 opacity-80" aria-hidden />
            Ver sitio público
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}